import { z } from 'zod';
import { Production } from '../../../__types__';
import type { ValidatedRow } from 'v5/platform/row-level-validation';
import { CoreFieldFilters } from 'v5/core/core-fields/core-fields-filters.types';
import {
  coreFieldFiltersSchema,
  strToNull
} from 'v5/core/core-fields/core-fields-filters.schemas';
import {
  EnumFilter,
  enumFilterSchema
} from 'v5/api/_common/query/filters/EnumFilter';
import {
  PaginatedResponse,
  paginationSchema,
  paginationUtils
} from '../_common/query/pagination';
import {
  withBuilderUtils,
  WithFilters,
  withFiltersUtils,
  WithOptionalFilters,
  WithPagination,
  withPaginationUtils,
  WithSearch,
  withSearchUtils,
  WithSorter,
  withSorterUtils
} from '../_common/query/queryUtils';
import { WithWorkspace } from '../_common/query/workspace';
import {
  WithWorkspaceScoping,
  withWorkspaceScoping,
  workspaceScopingQueryUtils
} from '../_common/query/workspace-scoping';
import {
  WithTeamScoping,
  withTeamScoping,
  teamScopingQueryUtils
} from '../_common/query/team-scoping';
import { WithCustomFieldFilters } from 'v5/core/custom-fields/custom-fields-filters.types';
import { withCustomFieldFiltersSchema } from 'v5/core/custom-fields/custom-fields-filters.schemas';
export type ProductionBatchIdsQuery = WithFilters<{ id: EnumFilter<number> }>;

/**
 * Sort field names for production list — single source of truth for URL/query/schema
 * and for {@link SortableEntityTable} column `field` when `sortable: true`.
 * - `createdAt` — creation timestamp (direct column)
 * - `title` — production title (JSONB attribute)
 * - `code` — production code (JSONB attribute)
 * - `productionDate` — production start date (JSONB attribute)
 * - `status` — status ID (JSONB attribute; sorts by ID, not status name/order)
 *
 * Additional sortable fields available in backend (can be added as needed):
 * - updatedAt, productionTypeId, userId, productionEndDate, etc.
 * Add them to {@link PRODUCTION_SORT_FIELDS} and the Zod schema when exposing them here.
 */
export const PRODUCTION_SORT_FIELDS = [
  'createdAt',
  'title',
  'code',
  'productionDate',
  'status'
] as const;

export type ProductionSortField = (typeof PRODUCTION_SORT_FIELDS)[number];

/**
 * Stable column `field` for the productions list: sortable keys match
 * {@link ProductionSortField}; other keys are non-sortable column identifiers.
 */
export type ProductionTableColumnField =
  | ProductionSortField
  | 'tags'
  | 'crew'
  | 'userId'
  | 'resourcingBudget';

/**
 * Production list search query. Sort fields are exactly those in {@link PRODUCTION_SORT_FIELDS}
 * (see that constant for semantics per field).
 */
export type ProductionSearchQuery = WithSearch &
  WithPagination &
  WithSorter<ProductionSortField> &
  WithOptionalFilters<
    WithCustomFieldFilters<
      CoreFieldFilters & {
        userId: EnumFilter<string>;
        productionTypeId: EnumFilter<number>;
      }
    >
  >;

export type ProductionScopedSearchQuery = WithTeamScoping<
  WithWorkspaceScoping<ProductionSearchQuery>
>;

export type ProductionListSearchQuery =
  | ProductionSearchQuery
  | ProductionScopedSearchQuery;

export function asGlobalProductionSearchQuery<T extends ProductionSearchQuery>(
  searchQuery: T
): ProductionScopedSearchQuery {
  return withTeamScoping(
    withWorkspaceScoping(searchQuery, workspaceScopingQueryUtils.ofGlobal()),
    teamScopingQueryUtils.ofGlobal()
  );
}

export type ProductionQuery = ProductionBatchIdsQuery | ProductionSearchQuery;

/** Paginated list: each row is validated in {@link createProductionApi} (HTTP row parsing). */
export type ProductionListResponse = PaginatedResponse<
  ValidatedRow<Production>
>;

export type ProductionAssociationNote = {
  id: number;
  note: string;
  created_at: string;
  /** Account / user id for {@link SystemUser} resolution */
  user_id?: string | number;
  user?: Record<string, unknown>;
  archived?: boolean;
};

export type ProductionAssociationDocument = {
  id: number;
  url?: string;
  archived?: boolean;
  order?: number | null;
  file?: {
    id?: string;
    name: string;
    mime_type?: string;
  };
};

export type ProductionAssociationsBundle = {
  notes: ProductionAssociationNote[];
  documents: ProductionAssociationDocument[];
  /** Full payload for v1 Redux sync */
  raw: Record<string, unknown>;
};

export interface TransferProductionsToScopeRequest {
  ids: number[];
  enterprise_workspace_id: number;
  enterprise_team_id: number;
}

export interface ProductionApi {
  getProductions: (
    query: WithWorkspace<ProductionQuery | ProductionScopedSearchQuery>
  ) => Promise<ProductionListResponse>;
  getProduction(id: Production['id']): Promise<Production>;
  getProductionAssociations(
    productionId: Production['id']
  ): Promise<ProductionAssociationsBundle>;
  createProductionNote(
    productionId: Production['id'],
    payload: { note: string }
  ): Promise<ProductionAssociationNote>;
  deleteProductionNote(
    productionId: Production['id'],
    noteId: number
  ): Promise<void>;
  createProductionDocument(
    productionId: Production['id'],
    payload: { file_id: string; url: string }
  ): Promise<ProductionAssociationDocument>;
  deleteProductionDocument(
    productionId: Production['id'],
    documentId: number
  ): Promise<void>;
  reorderDocuments(orderedIds: number[]): Promise<void>;
  transferProductionsToScope(
    request: TransferProductionsToScopeRequest,
    signal?: AbortSignal
  ): Promise<void>;
}

export const productionQueryUtils = withBuilderUtils({
  ...withSearchUtils<ProductionSearchQuery>(),
  ...withFiltersUtils<ProductionSearchQuery>(),
  ...withPaginationUtils<ProductionSearchQuery>(),
  ...withSorterUtils<ProductionSearchQuery>(),
  ofEmpty: (): ProductionBatchIdsQuery => ({
    filters: { id: null }
  }),
  ofEmptySearchQuery: (): ProductionSearchQuery => ({
    search: '',
    sort: { field: 'createdAt', order: 'desc' },
    page: paginationUtils.ofDefault(),
    filters: {}
  }),
  ofDefault: (): ProductionSearchQuery => ({
    search: '',
    sort: { field: 'productionDate', order: 'desc' },
    page: paginationUtils.ofDefault(),
    filters: {
      archived: { operator: 'equal', value: false }
    }
  })
}).setInitialQueryFactory(x => x.ofEmptySearchQuery);

export const productionSearchQuerySchema = z
  .object({
    search: z.preprocess(value => value || '', z.string()).default(''),
    filters: withCustomFieldFiltersSchema(
      coreFieldFiltersSchema.extend({
        userId: z.preprocess(
          strToNull,
          enumFilterSchema(z.string()).nullable()
        ),
        productionTypeId: z.preprocess(
          strToNull,
          enumFilterSchema(z.coerce.number()).nullable()
        )
      })
    ).partial(),
    page: paginationSchema,
    sort: z
      .preprocess(
        strToNull,
        z
          .object({
            field: z.enum(PRODUCTION_SORT_FIELDS),
            order: z.enum(['asc', 'desc'])
          })
          .nullable()
      )
      .default(null)
  })
  .catch(error => {
    console.error('Error parsing production search query', error.issues);
    return productionQueryUtils.ofEmptySearchQuery();
  });

// todo: make sure we can serialize and deserialize the query properly using these schemas
// todo: check if fallback from local storage works properly from history interceptor
// todo: find or create new pagination component
// todo: bring all useful filters

export const productionBatchGetIdsQuerySchema = z
  .object({
    filters: z.object({
      id: z
        .preprocess(strToNull, enumFilterSchema(z.coerce.number()).nullable())
        .prefault(null)
    })
  })
  .catch(productionQueryUtils.ofEmpty);

export const productionQuerySchema = z.union([
  productionSearchQuerySchema,
  productionBatchGetIdsQuerySchema
]);
